import { forwardRef, type ComponentPropsWithoutRef, type ReactNode } from 'react';

import { useId } from '@lib/hooks';
import { cn, cva, type VariantProps } from '@lib/utils';
import { InputErrorMsg } from './InputErrorMsg';

const textareaVariants = cva(
  [
    // Base
    'block w-full appearance-none bg-transparent tracking-wide resize-none',
    // Typography
    'text-fg placeholder:font-light placeholder:text-fg-muted',
    // Focus
    'outline-hidden focus:outline-hidden',
    // Transitions
    'transition-colors duration-200 ease-in-out',
    // Disabled
    'disabled:select-none',
  ],
  {
    variants: {
      variant: {
        default: [
          // Layout
          'rounded-lg border px-3 py-2 text-start',
          // Border color
          'border-line',
          // Hover
          'hover:border-line-hover',
          // Focus (uses primary context)
          'this:primary focus:border-this-solid',
          // Peer class for styling
          'peer',
        ],
      },
      size: {
        xs: 'text-xs px-2 py-1',
        sm: 'text-sm px-2.5 py-1.5',
        md: 'text-sm px-3 py-2',
        lg: 'text-base px-3.5 py-2.5',
        xl: 'text-base px-4 py-3',
      },
      hasError: {
        true: 'this:error border-this-solid',
        false: '',
      },
      disabled: {
        true: ['cursor-not-allowed opacity-60', 'bg-surface-raised border-line'],
        false: '',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'md',
      hasError: false,
      disabled: false,
    },
    compoundVariants: [
      // Override border color when hasError is true (takes precedence over default hover)
      {
        hasError: true,
        className: 'hover:border-this-solid',
      },
    ],
  }
);

const labelVariants = cva(['text-sm font-medium text-fg']);

const descriptionVariants = cva(['mt-1 text-xs text-fg-muted']);

type TextareaProps = {
  label?: ReactNode;
  description?: ReactNode;
  classNames?: {
    root?: string;
    label?: string;
    labelText?: string;
    wrapper?: string;
    input?: string;
    error?: string;
    description?: string;
  };
  error?: boolean | ReactNode;
  rootProps?: Record<string, unknown>;
  labelProps?: Record<string, unknown>;
} & Omit<VariantProps<typeof textareaVariants>, 'hasError'> &
  Omit<ComponentPropsWithoutRef<'textarea'>, 'size'>;

const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  (
    {
      label,
      description,
      className,
      classNames = {},
      error,
      disabled = false,
      rootProps,
      labelProps,
      id,
      size = 'md',
      variant = 'default',
      ...rest
    },
    ref
  ) => {
    const inputId = useId(id, 'textarea');
    const hasError = !!error;

    return (
      <div className={cn('flex flex-col', classNames.root)} {...rootProps}>
        {label && (
          <label
            htmlFor={inputId}
            className={cn(labelVariants(), classNames.label)}
            {...labelProps}
          >
            <span className={classNames.labelText}>{label}</span>
          </label>
        )}

        <div className={cn('relative', label && 'mt-1.5', classNames.wrapper)}>
          <textarea
            className={cn(
              textareaVariants({ variant, size, hasError, disabled }),
              className,
              classNames.input
            )}
            id={inputId}
            ref={ref}
            disabled={disabled}
            {...rest}
          />
        </div>
        <InputErrorMsg when={hasError && typeof error !== 'boolean'} className={classNames.error}>
          {error}
        </InputErrorMsg>
        {description && (
          <span className={cn(descriptionVariants(), classNames.description)}>{description}</span>
        )}
      </div>
    );
  }
);

Textarea.displayName = 'Textarea';

export { Textarea, textareaVariants };
export type { TextareaProps };
